import { ExclamationCircleIcon } from "@heroicons/react/20/solid";
import { FC, Fragment, InputHTMLAttributes, useState } from "react";

export type InputWithCornerHintprops = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  span?: string;
  error?: boolean | any;
  textDescribe?: string | any;
  textError?: string | any;
  onError?: (error: boolean) => void; // Cambiado a boolean
};

export const InputWithCornerHint: FC<InputWithCornerHintprops> = ({
  label,
  span,
  error: externalError,
  textDescribe,
  textError,
  className,
  onError,
  type,
  ...props
}) => {
  const [internalError, setInternalError] = useState(false);
  const [touched, setTouched] = useState(false);

  // Expresión regular para email
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

  // Validación según el tipo
  const validateInput = (value: string) => {
    if (type === "email") {
      return value ? emailRegex.test(value) : false;
    }
    if (type === "number") {
      return /^\d+$/.test(value);
    }
    return true;
  };

  // Maneja el blur para validar y activar el borde de error
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTouched(true);
    const isValid = validateInput(e.target.value);
    setInternalError(!isValid);
    onError?.(!isValid); // Llama a onError con true si no es válido, false si es válido
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  // Maneja la entrada para type="number" y valida en tiempo real
  const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
    if (type === "number") {
      const value = e.currentTarget.value;
      if (!/^\d*$/.test(value)) {
        e.currentTarget.value = value.replace(/\D/g, ""); // Elimina no dígitos
      }
    }
    const isValid = validateInput(e.currentTarget.value);
    setInternalError(!isValid);
    onError?.(!isValid); // Llama a onError en tiempo real
    if (props.onInput) {
      props.onInput(e);
    }
  };

  // Combinamos error externo e interno
  const hasError = externalError || (internalError && touched);

  return (
    <Fragment>
      <div className={className}>
        <div className="flex justify-between">
          <label
            htmlFor={props.id}
            className="block text-sm font-medium leading-6 text-gray-600"
          >
            {label}
          </label>
          <span
            aria-describedby={props.id}
            className={`text-sm leading-6 text-gray-500`}
          >
            {span}
          </span>
        </div>
        <div className="relative">
          <input
            {...props}
            type={type}
            onBlur={handleBlur}
            onInput={handleInput}
            className={`px-2 block w-full rounded-md border-0 py-1.5 text-gray-400 shadow-sm ring-1 ring-inset ${
              hasError
                ? "ring-red-300 placeholder:text-red-300"
                : "ring-gray-300 placeholder:text-gray-400"
            } focus:ring-2 focus:ring-inset ${
              hasError ? "focus:ring-red-500" : "focus:ring-primary"
            } disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 disabled:ring-gray-200 sm:text-sm sm:leading-6`}
          />
          {hasError ? (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ExclamationCircleIcon
                aria-hidden="true"
                className="h-5 w-5 text-red-500"
              />
            </div>
          ) : null}
        </div>
        {hasError ? (
          <p className="mt-1 text-sm text-red-600">
            {textError ||
              (type === "email"
                ? "Please enter a valid email address"
                : type === "number"
                  ? "Please enter a valid number"
                  : "Invalid input")}
          </p>
        ) : null}
        {textDescribe && (
          <p className={`mt-1 text-sm text-gray-400`}>{textDescribe}</p>
        )}
      </div>
    </Fragment>
  );
};
